<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 d-inline-block mb-0">{{ $t("capacity.title") }}</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button
            v-if="isDtacAdmin"
            size="md"
            type="neutral"
            @click="runExport"
            :disabled="exporting"
          >
            {{ $t("export.button") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <modal :show.sync="exporting" :show-close="false" modal-classes="modal-sm">
      <div class="text-center py-3">
        <i class="fa fa-spinner fa-spin fa-2x text-primary mb-2"></i>
        <h4 class="mb-1">{{ $t("export.processing") }}</h4>
        <p class="text-muted mb-0">{{ $t("export.processing_text") }}</p>
      </div>
    </modal>

    <modal
      :show="exportError !== null"
      @close="clearExportError"
      modal-classes="modal-secondary"
    >
      <template slot="header">
        <h5 class="modal-title text-danger">
          <i class="fa fa-exclamation-triangle mr-2"></i>
          {{ exportError && exportError.title }}
        </h5>
      </template>
      <pre
        v-if="exportError && exportError.detail"
        class="export-error-detail"
      >{{ exportError.detail }}</pre>
      <template slot="footer">
        <button type="button" class="btn btn-link ml-auto" @click="clearExportError">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <div class="container-fluid mt--6">
      <!-- Per-server cards. col-md-3 = max 4 per row. wraps automatically. -->
      <div class="row">
        <div
          class="col-md-3 col-sm-6"
          v-for="row in rows"
          :key="row.Name"
        >
          <stats-card
            :title="row.DisplayName || row.Name"
            type="gradient-default"
            :sub-title="row.Used + ' / ' + row.Capacity"
            :icon="iconFor(row)"
            class="mb-4"
          >
            <template slot="footer">
              <div class="progress mb-2" style="height: 6px">
                <div
                  class="progress-bar"
                  :class="barClass(row)"
                  :style="{ width: pct(row) + '%' }"
                ></div>
              </div>
              <span :class="pctClass(row)" class="font-weight-bold">
                {{ pct(row) }}%
              </span>
              <span class="text-nowrap">{{ $t("capacity.used") }}</span>
            </template>
          </stats-card>
        </div>
      </div>

      <!-- System-wide aggregate bar. -->
      <card class="mb-4" body-classes="py-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="text-muted">{{ $t("capacity.system_total") }}</span>
          <span class="font-weight-bold">
            {{ totalUsed }} / {{ totalCapacity }}
            <span class="text-muted">({{ totalPct }}%)</span>
          </span>
        </div>
        <div class="progress" style="height: 8px">
          <div
            class="progress-bar"
            :class="totalBarClass"
            :style="{ width: totalPct + '%' }"
          ></div>
        </div>
      </card>

      <!-- Per-server detail table. -->
      <card class="no-border-card" body-classes="px-0 pb-1">
        <template slot="header">
          <h3 class="mb-0">{{ $t("capacity.detail") }}</h3>
        </template>
        <el-table :data="rows" header-row-class-name="thead-light">
          <el-table-column
            :label="$t('capacity.server')"
            prop="Name"
            align="center"
            min-width="120"
          >
            <template slot-scope="{ row }">
              {{ row.DisplayName || row.Name }}
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('capacity.corporates')"
            prop="Corporates"
            align="center"
            min-width="110"
          ></el-table-column>
          <el-table-column
            :label="$t('capacity.pilot_numbers')"
            prop="PilotNumbers"
            align="center"
            min-width="140"
          ></el-table-column>
          <el-table-column
            :label="$t('capacity.agent_numbers')"
            prop="AgentNumbers"
            align="center"
            min-width="140"
          ></el-table-column>
          <el-table-column
            :label="$t('capacity.used')"
            prop="Used"
            align="center"
            min-width="100"
          ></el-table-column>
          <el-table-column
            :label="$t('capacity.capacity')"
            prop="Capacity"
            align="center"
            min-width="120"
          ></el-table-column>
          <el-table-column
            :label="$t('capacity.available')"
            align="center"
            min-width="120"
          >
            <template slot-scope="{ row }">
              <span :class="row.Available <= 0 ? 'text-danger' : ''">{{
                row.Available
              }}</span>
            </template>
          </el-table-column>
        </el-table>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import StatsCard from "@/components/Cards/StatsCard";
import Modal from "@/components/Modal";
import exportMixin from "./exportMixin";
import { WebPortalService } from "../../resource";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "capacity-report",
  mixins: [exportMixin],
  components: {
    RouteBreadCrumb,
    StatsCard,
    Modal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  data() {
    return {
      exportFn: () => WebPortalService.CapacityExportV4()
    };
  },
  computed: {
    ...mapGetters({
      rows: "Capacity/getData",
      isDtacAdmin: "Auth/isDtacAdmin"
    }),
    totalCapacity() {
      return this.rows.reduce((s, r) => s + (r.Capacity || 0), 0);
    },
    totalUsed() {
      return this.rows.reduce((s, r) => s + (r.Used || 0), 0);
    },
    totalPct() {
      if (this.totalCapacity === 0) return 0;
      return Math.round((this.totalUsed * 1000) / this.totalCapacity) / 10;
    },
    totalBarClass() {
      if (this.totalPct >= 90) return "bg-danger";
      if (this.totalPct >= 70) return "bg-warning";
      return "bg-success";
    }
  },
  mounted() {
    this.listData();
  },
  methods: {
    ...mapActions({
      listData: "Capacity/listData"
    }),
    pct(row) {
      if (!row.Capacity) return 0;
      return Math.round((row.Used * 1000) / row.Capacity) / 10;
    },
    barClass(row) {
      const p = this.pct(row);
      if (p >= 90) return "bg-danger";
      if (p >= 70) return "bg-warning";
      return "bg-success";
    },
    pctClass(row) {
      const p = this.pct(row);
      if (p >= 90) return "text-danger";
      if (p >= 70) return "text-warning";
      return "text-success";
    },
    iconFor(row) {
      const p = this.pct(row);
      if (p >= 90) return "fa fa-server text-danger";
      if (p >= 70) return "fa fa-server text-warning";
      return "fa fa-server text-success";
    }
  }
};
</script>
<style scoped>
.export-error-detail {
  background: #fff5f7;
  border: 1px solid #f5365c;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 60vh;
  overflow-y: auto;
  margin: 0;
}
</style>
